import { getGithubUrl } from '@bangle.io/base-utils';
import { useLogger } from '@bangle.io/context';
import { TypoCat } from '@bangle.io/ui-components';
import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { NoticeView } from './notice-view';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
}

/** Catches JavaScript errors anywhere in its child component tree and displays a fallback UI using NoticeView. */
export function ErrorBoundary({ children }: ErrorBoundaryProps) {
  const logger = useLogger();
  return (
    <ReactErrorBoundary
      onError={(error, errorInfo) => {
        logger.error('ErrorBoundary caught error:', error, errorInfo);
      }}
      fallbackRender={({ error }) => {
        const reportError = toError(error);

        return (
          <NoticeView
            title={t.app.common.somethingWentWrong}
            description={
              <span className="text-destructive text-sm">
                {reportError.message}
              </span>
            }
            illustration={
              <TypoCat
                pose="scared-404"
                className="mx-auto h-32 w-auto"
                decorative
              />
            }
            actions={[
              {
                label: t.app.common.report,
                variant: 'outline',
                onClick: () => {
                  window.open(getGithubUrl(reportError, logger), '_blank');
                },
              },
            ]}
          />
        );
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
